/**
 * Pagina Admin — configurazione sistema (E10.11 — RF-7-1 + RF-12-1).
 *
 * Route: /admin
 *
 * Sezioni:
 * 1. Soglia notifiche (RF-7-1): GET/PUT /settings/threshold sul notification-service.
 *    Mostra il valore corrente e permette di cambiarlo tramite select.
 *    Azioni gated SYSTEM_CONFIGURATION.
 *
 * 2. Permessi portale per ruolo: GET/PUT /api/products/admin/ui-capabilities
 *    sul product-service. Matrice capacita × ruoli con checkbox.
 *    Azioni gated SYSTEM_CONFIGURATION; non-admin vede read-only.
 *
 * 0. Strumenti dell'ambiente (solo admin): link "Mailpit" e "DTUI" + copia della password admin
 *    di Dependency-Track (GET /api/products/admin/tools[/dt-admin-password]). Opt-in per ambiente:
 *    in produzione nessun link è configurato e la sezione lo dice.
 *
 * SOLO DEV — DA RIMUOVERE PRIMA DELLA PRODUZIONE: contenuto integrale del file .env del server
 *    (GET /api/products/admin/tools/env-file), solo admin e solo se il backend ha
 *    ADMIN_ENV_VIEWER=1 (impostato unicamente da deploy/compose.dev-ports.yml).
 *
 * 4. Soglia Batch Edit: GET/PUT /api/products/admin/batch-edit-settings. Un run della Batch Edit
 *    API con N modifiche (nuovi + aggiornamenti + cancellazioni) ≥ soglia va «In attesa di
 *    conferma». Intero 1..1.000.000, default 10. Azioni gated SYSTEM_CONFIGURATION.
 *
 * 5. Nota per il cliente dei Ticket: GET/PUT /api/products/admin/ticket-settings. Chiudendo un
 *    ticket manuale come "non impattato" la nota per chi ha segnalato deve avere almeno N parole
 *    (parole >= N). Intero 1..2000, default 100. Azioni gated SYSTEM_CONFIGURATION.
 *
 * 3. Chiave NVD (RF-12-1): RF-12-1 è nativo Dependency-Track.
 *    Il portale NON chiama alcun endpoint proprio: mostra solo istruzioni e link
 *    al pannello Administration → Vulnerability Sources → NVD di Dependency-Track.
 *    La chiave API NVD va configurata direttamente nell'UI di DT.
 */

import { Fragment, useState } from 'react';
import { usePermissions } from '../../auth/index.js';
import { ApiError } from '../../api/client.js';
import {
  useThreshold,
  useUpdateThreshold,
  useUiCapabilities,
  useUpdateUiCapabilities,
  useRolePermissions,
  useUpdateRolePermissions,
  useCreateRole,
  usePermissionsCatalog,
  useBranding,
  useUpdateBranding,
  usePrefectDeployments,
  useUpdateDeploymentSchedule,
  useDeleteProductCatalog,
  useAdminTools,
  useAdminEnvFile,
  useBatchEditSettings,
  useUpdateBatchEditHoldThreshold,
  useTicketSettings,
  useUpdateCustomerNoteMinWords,
} from './hooks.js';
import { fetchDtAdminPassword, resolveToolUrl } from './tools-api.js';
import { CopyButton } from '../../components/CopyButton.js';
import type { NotificationSeverity } from './types.js';
import type { PrefectDeployment } from './prefect-schedules-types.js';
import { UI_CAPABILITIES, UI_CAPABILITY_LABELS } from './capabilities-types.js';
import type { RoleCapabilitiesMap, UiCapability } from './capabilities-types.js';
import {
  TC_PERMISSION_LABELS,
  groupCanonicalByResource,
  canonicalOperationLabel,
} from './role-permissions-types.js';
import type { RolePermissionsMap } from './role-permissions-types.js';
import type { CraRole, TcPermission } from '../../auth/types.js';
import styles from './Admin.module.css';
import { SezioneSso } from './SezioneSso.js';

/** Valori di severità ammessi per la select. */
const SEVERITY_OPTIONS: NotificationSeverity[] = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFO',
  'UNASSIGNED',
];

// ---------------------------------------------------------------------------
// Sezione 1: soglia notifiche
// ---------------------------------------------------------------------------

function SezioneThreshold({ puoConfigurare }: { puoConfigurare: boolean }) {
  // La query è abilitata solo se l'utente ha i permessi (evita fetch inutile)
  const { data, isLoading, isError, error: queryError } = useThreshold(puoConfigurare);
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
  } = useUpdateThreshold();

  // Valore locale della select (inizializzato al valore corrente dal server)
  const [selectedSeverity, setSelectedSeverity] = useState<NotificationSeverity | ''>('');

  // Sincronizza il valore locale quando arriva la risposta server
  const currentValue: NotificationSeverity = data?.severityThreshold ?? 'HIGH';
  const displayed = selectedSeverity !== '' ? selectedSeverity : currentValue;

  function handleSalva() {
    if (!selectedSeverity) return;
    mutate(selectedSeverity);
  }

  return (
    <div className={styles.section} data-testid="sezione-threshold">
      <p className={styles.sectionTitle}>Soglia notifiche (RF-7-1)</p>
      <p className={styles.sectionSubtitle}>
        Definisce la severità minima sopra la quale il notification-service invia notifiche interne.
      </p>

      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="threshold-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="threshold-loading">
          Caricamento configurazione...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="threshold-error">
          {queryError instanceof ApiError
            ? queryError.message
            : (queryError?.message ?? 'Errore caricamento configurazione')}
        </div>
      )}

      {!isLoading && !isError && puoConfigurare && (
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formGroupLabel} htmlFor="select-severity-threshold">
              Soglia corrente
            </label>
            <select
              id="select-severity-threshold"
              className={styles.formSelect}
              value={displayed}
              onChange={(e) => {
                setSelectedSeverity(e.target.value as NotificationSeverity);
              }}
              data-testid="select-threshold"
              disabled={isPending}
            >
              {SEVERITY_OPTIONS.map((sev) => (
                <option key={sev} value={sev}>
                  {sev}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSalva}
            disabled={isPending || !selectedSeverity || selectedSeverity === currentValue}
            data-testid="btn-salva-threshold"
          >
            {isPending ? 'Salvataggio...' : 'Salva'}
          </button>
          {mutError && (
            <span className={styles.inlineMutError} data-testid="threshold-mut-error">
              {mutErrObj instanceof ApiError
                ? mutErrObj.message
                : (mutErrObj?.message ?? 'Errore salvataggio')}
            </span>
          )}
          {isSuccess && (
            <span className={styles.successMsg} data-testid="threshold-success">
              Soglia aggiornata.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: soglia di hold della Batch Edit API
// ---------------------------------------------------------------------------

const SOURCE_LABEL: Record<string, string> = {
  admin: 'impostata da /admin',
  env: 'default di ambiente (BATCH_EDIT_HOLD_THRESHOLD)',
  default: 'default',
};

/** Valida l'input: intero tra min e max. Ritorna il messaggio d'errore o null. */
export function holdThresholdError(raw: string, min: number, max: number): string | null {
  const v = raw.trim();
  if (v === '') return 'Inserisci un numero.';
  if (!/^\d+$/.test(v)) return 'Serve un numero intero (senza decimali).';
  const n = Number(v);
  if (n < min || n > max) {
    return `Valore ammesso: da ${min.toLocaleString('it-IT')} a ${max.toLocaleString('it-IT')}.`;
  }
  return null;
}

export function SezioneBatchEditThreshold({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data: raw, isLoading, isError, error } = useBatchEditSettings(puoConfigurare);
  // Risposta non conforme (es. backend senza l'endpoint dietro un proxy): sezione non popolata.
  const data = raw && typeof raw.holdThreshold === 'number' ? raw : undefined;
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
    reset,
  } = useUpdateBatchEditHoldThreshold();
  // null = nessuna modifica: l'input mostra il valore corrente.
  const [draft, setDraft] = useState<string | null>(null);

  const current = data?.holdThreshold;
  const value = draft ?? (current !== undefined ? String(current) : '');
  const errore = data && draft !== null ? holdThresholdError(draft, data.min, data.max) : null;
  const modificato = draft !== null && !errore && Number(draft.trim()) !== current;

  function salva() {
    if (!modificato) return;
    mutate(Number(draft!.trim()), { onSuccess: () => setDraft(null) });
  }

  return (
    <div className={styles.section} data-testid="sezione-batch-threshold">
      <p className={styles.sectionTitle}>Soglia Batch Edit (attesa di conferma)</p>
      <p className={styles.sectionSubtitle}>
        Un batch della Batch Edit API parte automaticamente soltanto se le sue modifiche (nuovi +
        aggiornamenti + cancellazioni, gli invariati non contano) sono <strong>meno</strong> della
        soglia. Con un numero di modifiche uguale o superiore resta «In attesa di conferma» finché
        un utente non lo forza da /batch-edits e gli amministratori ricevono un avviso.
      </p>

      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="batch-threshold-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}

      {puoConfigurare && isLoading && (
        <p className={styles.loadingMsg} data-testid="batch-threshold-loading">
          Caricamento configurazione...
        </p>
      )}

      {puoConfigurare && isError && (
        <div className={styles.errorMsg} data-testid="batch-threshold-error">
          {error instanceof ApiError
            ? error.message
            : (error?.message ?? 'Errore caricamento soglia')}
        </div>
      )}

      {puoConfigurare && data && (
        <>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="input-batch-threshold">
                Soglia (modifiche per batch)
              </label>
              <input
                id="input-batch-threshold"
                type="number"
                inputMode="numeric"
                min={data.min}
                max={data.max}
                step={1}
                className={styles.formSelect}
                value={value}
                onChange={(e) => {
                  reset();
                  setDraft(e.target.value);
                }}
                aria-invalid={errore ? true : undefined}
                aria-describedby="batch-threshold-help"
                disabled={isPending}
                data-testid="input-batch-threshold"
              />
            </div>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={salva}
              disabled={isPending || !modificato}
              data-testid="btn-salva-batch-threshold"
            >
              {isPending ? 'Salvataggio...' : 'Salva'}
            </button>
            {errore && (
              <span
                className={styles.inlineMutError}
                role="alert"
                data-testid="batch-threshold-invalid"
              >
                {errore}
              </span>
            )}
            {mutError && (
              <span className={styles.inlineMutError} data-testid="batch-threshold-mut-error">
                {mutErrObj instanceof ApiError
                  ? mutErrObj.message
                  : (mutErrObj?.message ?? 'Errore salvataggio')}
              </span>
            )}
            {isSuccess && (
              <span className={styles.successMsg} data-testid="batch-threshold-success">
                Soglia aggiornata: vale dal prossimo run.
              </span>
            )}
          </div>
          <p
            className={styles.sectionSubtitle}
            id="batch-threshold-help"
            data-testid="batch-threshold-current"
          >
            Valore attuale: <strong>{data.holdThreshold.toLocaleString('it-IT')}</strong> (
            {SOURCE_LABEL[data.source] ?? data.source}
            {data.updatedBy ? `, da ${data.updatedBy}` : ''}
            {data.updatedAt ? ` il ${new Date(data.updatedAt).toLocaleString('it-IT')}` : ''}) · con{' '}
            {data.holdThreshold.toLocaleString('it-IT')} si esegue fino a{' '}
            {(data.holdThreshold - 1).toLocaleString('it-IT')} modifiche · default{' '}
            {data.defaultHoldThreshold.toLocaleString('it-IT')}.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: parole minime della nota per il cliente (chiusura dei Ticket manuali)
// ---------------------------------------------------------------------------

const NOTE_SOURCE_LABEL: Record<string, string> = {
  admin: 'impostato da /admin',
  default: 'default',
};

export function SezioneTicketNotaCliente({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data: raw, isLoading, isError, error } = useTicketSettings(puoConfigurare);
  // Risposta non conforme (es. backend senza l'endpoint dietro un proxy): sezione non popolata.
  const data = raw && typeof raw.customerNoteMinWords === 'number' ? raw : undefined;
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
    reset,
  } = useUpdateCustomerNoteMinWords();
  // null = nessuna modifica: l'input mostra il valore corrente.
  const [draft, setDraft] = useState<string | null>(null);

  const current = data?.customerNoteMinWords;
  const value = draft ?? (current !== undefined ? String(current) : '');
  const errore = data && draft !== null ? holdThresholdError(draft, data.min, data.max) : null;
  const modificato = draft !== null && !errore && Number(draft.trim()) !== current;

  function salva() {
    if (!modificato) return;
    mutate(Number(draft!.trim()), { onSuccess: () => setDraft(null) });
  }

  return (
    <div className={styles.section} data-testid="sezione-ticket-nota-cliente">
      <p className={styles.sectionTitle}>Ticket: nota per il cliente alla chiusura</p>
      <p className={styles.sectionSubtitle}>
        Chiudendo un ticket <strong>creato manualmente</strong> come «non impattato»,
        l&apos;analista scrive una comunicazione che viene inviata via email a chi ha segnalato il
        ticket. Il pulsante «Chiudi» resta disabilitato finché la nota non raggiunge{' '}
        <strong>almeno</strong> questo numero di parole (parole separate da spazi); il server
        applica la stessa regola. I ticket automatici non hanno la nota.
      </p>

      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="nota-cliente-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}

      {puoConfigurare && isLoading && (
        <p className={styles.loadingMsg} data-testid="nota-cliente-loading">
          Caricamento configurazione...
        </p>
      )}

      {puoConfigurare && isError && (
        <div className={styles.errorMsg} data-testid="nota-cliente-error">
          {error instanceof ApiError
            ? error.message
            : (error?.message ?? 'Errore caricamento impostazione')}
        </div>
      )}

      {puoConfigurare && data && (
        <>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="input-nota-cliente-min-parole">
                Parole minime della nota
              </label>
              <input
                id="input-nota-cliente-min-parole"
                type="number"
                inputMode="numeric"
                min={data.min}
                max={data.max}
                step={1}
                className={styles.formSelect}
                value={value}
                onChange={(e) => {
                  reset();
                  setDraft(e.target.value);
                }}
                aria-invalid={errore ? true : undefined}
                aria-describedby="nota-cliente-help"
                disabled={isPending}
                data-testid="input-nota-cliente-min-parole"
              />
            </div>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={salva}
              disabled={isPending || !modificato}
              data-testid="btn-salva-nota-cliente-min-parole"
            >
              {isPending ? 'Salvataggio...' : 'Salva'}
            </button>
            {errore && (
              <span
                className={styles.inlineMutError}
                role="alert"
                data-testid="nota-cliente-min-parole-invalid"
              >
                {errore}
              </span>
            )}
            {mutError && (
              <span className={styles.inlineMutError} data-testid="nota-cliente-mut-error">
                {mutErrObj instanceof ApiError
                  ? mutErrObj.message
                  : (mutErrObj?.message ?? 'Errore salvataggio')}
              </span>
            )}
            {isSuccess && (
              <span className={styles.successMsg} data-testid="nota-cliente-success">
                Impostazione aggiornata: vale dalla prossima chiusura.
              </span>
            )}
          </div>
          <p
            className={styles.sectionSubtitle}
            id="nota-cliente-help"
            data-testid="nota-cliente-current"
          >
            Valore attuale: <strong>{data.customerNoteMinWords.toLocaleString('it-IT')}</strong>{' '}
            parole ({NOTE_SOURCE_LABEL[data.source] ?? data.source}
            {data.updatedBy ? `, da ${data.updatedBy}` : ''}
            {data.updatedAt ? ` il ${new Date(data.updatedAt).toLocaleString('it-IT')}` : ''}) ·
            ammessi da {data.min.toLocaleString('it-IT')} a {data.max.toLocaleString('it-IT')} ·
            default {data.defaultCustomerNoteMinWords.toLocaleString('it-IT')}.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione 2: permessi portale per ruolo (ui-capabilities)
// ---------------------------------------------------------------------------

/** Ruoli CRA mostrati come colonne della matrice permessi UI (ui-capabilities). */
const CRA_ROLES: readonly CraRole[] = [
  'security_analyst',
  'developer',
  'analyst_supervisor',
] as const;

/** Etichette italiane dei ruoli CRA per la matrice ui-capabilities. */
const CRA_ROLE_LABELS: Record<string, string> = {
  security_analyst: 'Analista sicurezza',
  developer: 'Sviluppatore',
  analyst_supervisor: 'Supervisore analisi',
};

/**
 * Etichette italiane per i ruoli nella matrice RBAC.
 * I ruoli custom sono visualizzati verbatim (nessuna etichetta).
 */
const ROLE_LABELS: Record<string, string> = {
  security_analyst: 'Analista sicurezza',
  developer: 'Sviluppatore',
  analyst_supervisor: 'Supervisore Analisti',
  admin: 'Admin',
};

/** Ruoli built-in che appaiono prima nella matrice RBAC (nell'ordine desiderato). */
const BUILTIN_ROLE_ORDER: readonly string[] = [
  'security_analyst',
  'developer',
  'analyst_supervisor',
  'admin',
];

/**
 * Ordina i ruoli: built-in prima (nell'ordine fisso), poi custom alfabeticamente.
 * Derivato da Object.keys(serverMap) al render.
 */
function sortRoles(keys: string[]): string[] {
  const builtins = BUILTIN_ROLE_ORDER.filter((r) => keys.includes(r));
  const customs = keys
    .filter((r) => !BUILTIN_ROLE_ORDER.includes(r))
    .sort((a, b) => a.localeCompare(b));
  return [...builtins, ...customs];
}

function SezionePermessi({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data, isLoading, isError, error: queryError } = useUiCapabilities();
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
  } = useUpdateUiCapabilities();

  // Copia locale modificabile della mappa; inizializzata dal server
  const [localMap, setLocalMap] = useState<RoleCapabilitiesMap | null>(null);

  // Mappa effettivamente visualizzata: locale se modificata, altrimenti server
  const displayMap: RoleCapabilitiesMap | null = localMap ?? data ?? null;

  function handleToggle(capability: UiCapability, role: CraRole) {
    if (!puoConfigurare || !displayMap) return;
    const current = displayMap[role] ?? [];
    const updated = current.includes(capability)
      ? current.filter((c) => c !== capability)
      : [...current, capability];
    setLocalMap({ ...displayMap, [role]: updated });
  }

  function handleSalva() {
    if (!displayMap) return;
    mutate(displayMap, {
      onSuccess: () => {
        // Reset la copia locale: torna a mostrare i dati server aggiornati
        setLocalMap(null);
      },
    });
  }

  return (
    <div className={styles.section} data-testid="sezione-permessi">
      <p className={styles.sectionTitle}>Permessi portale per ruolo</p>
      <p className={styles.sectionSubtitle}>
        Definisce quali ruoli CRA hanno accesso alle funzionalità aggiuntive del portale.
      </p>

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="permessi-loading">
          Caricamento permessi...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="permessi-error">
          {queryError instanceof ApiError
            ? queryError.message
            : (queryError?.message ?? 'Errore caricamento permessi')}
        </div>
      )}

      {!isLoading && !isError && displayMap && (
        <>
          <table className={styles.capabilitiesTable} data-testid="permessi-matrice">
            <thead>
              <tr>
                <th>Funzionalita</th>
                {CRA_ROLES.map((role) => (
                  <th key={role}>{CRA_ROLE_LABELS[role]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {UI_CAPABILITIES.map((cap) => (
                <tr key={cap} data-testid={`permessi-row-${cap}`}>
                  <td>{UI_CAPABILITY_LABELS[cap]}</td>
                  {CRA_ROLES.map((role) => (
                    <td key={role}>
                      <input
                        type="checkbox"
                        checked={displayMap[role]?.includes(cap) ?? false}
                        onChange={() => handleToggle(cap, role)}
                        disabled={!puoConfigurare || isPending}
                        data-testid={`permessi-check-${cap}-${role}`}
                        aria-label={`${UI_CAPABILITY_LABELS[cap]} — ${CRA_ROLE_LABELS[role]}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {puoConfigurare && (
            <div className={styles.formRow}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSalva}
                disabled={isPending || localMap === null}
                data-testid="btn-salva-permessi"
              >
                {isPending ? 'Salvataggio...' : 'Salva'}
              </button>
              {mutError && (
                <span className={styles.inlineMutError} data-testid="permessi-mut-error">
                  {mutErrObj instanceof ApiError
                    ? mutErrObj.message
                    : (mutErrObj?.message ?? 'Errore salvataggio')}
                </span>
              )}
              {isSuccess && (
                <span className={styles.successMsg} data-testid="permessi-success">
                  Permessi aggiornati.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: privilegi RBAC per ruolo (role-permissions)
// ---------------------------------------------------------------------------

/** Una riga della matrice RBAC (privilegio × ruoli), condivisa tra la tabella canonica e quella legacy. */
function RbacRow({
  perm,
  label,
  orderedRoles,
  displayMap,
  puoConfigurare,
  isPending,
  onToggle,
}: {
  perm: TcPermission;
  label: string;
  orderedRoles: string[];
  displayMap: RolePermissionsMap;
  puoConfigurare: boolean;
  isPending: boolean;
  onToggle: (permission: TcPermission, role: string) => void;
}) {
  return (
    <tr data-testid={`rbac-row-${perm}`}>
      <td>{label}</td>
      {orderedRoles.map((role) => {
        // Colonna admin: tutti i checkbox disabilitati e spuntati (bypass hardwired).
        const adminLocked = role === 'admin';
        const checked = adminLocked || (displayMap[role]?.includes(perm) ?? false);
        return (
          <td key={role}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(perm, role)}
              disabled={!puoConfigurare || isPending || adminLocked}
              title={adminLocked ? 'Sempre tutti i privilegi' : undefined}
              data-testid={`rbac-check-${perm}-${role}`}
              aria-label={`${label} — ${ROLE_LABELS[role] ?? role}`}
            />
          </td>
        );
      })}
    </tr>
  );
}

function SezioneRbac({ puoConfigurare }: { puoConfigurare: boolean }) {
  // Solo gli admin (SYSTEM_CONFIGURATION) possono leggere/scrivere la mappa privilegi.
  const { data, isLoading, isError, error: queryError } = useRolePermissions(puoConfigurare);
  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
    error: catalogErrObj,
  } = usePermissionsCatalog(puoConfigurare);
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
  } = useUpdateRolePermissions();
  const createRoleMutation = useCreateRole();

  const isLoadingRbac = isLoading || catalogLoading;
  const isErrorRbac = isError || catalogError;
  const rbacQueryError = queryError ?? catalogErrObj;
  const canonicalGroups = groupCanonicalByResource(catalog?.canonical ?? []);
  const legacyPermissions = catalog?.legacy ?? [];

  // Copia locale modificabile della mappa; inizializzata dal server
  const [localMap, setLocalMap] = useState<RolePermissionsMap | null>(null);
  // Stato del form "Nuovo ruolo"
  const [newRoleName, setNewRoleName] = useState('');

  // Mappa effettivamente visualizzata: locale se modificata, altrimenti server
  const displayMap: RolePermissionsMap | null = localMap ?? data ?? null;

  // Colonne ordinate: built-in prima, poi custom alfabeticamente
  const orderedRoles: string[] = displayMap ? sortRoles(Object.keys(displayMap)) : [];

  function handleToggle(permission: TcPermission, role: string) {
    if (!puoConfigurare || !displayMap) return;
    // Colonna admin bloccata: tutti i privilegi hardwired, nessuna modifica.
    if (role === 'admin') return;
    const current = displayMap[role] ?? [];
    const updated = current.includes(permission)
      ? current.filter((p) => p !== permission)
      : [...current, permission];
    setLocalMap({ ...displayMap, [role]: updated });
  }

  function handleSalva() {
    if (!displayMap) return;
    // Strip admin dal payload: il server rifiuta la chiave admin nel corpo PUT.
    const { admin: _admin, ...mapSenzaAdmin } = displayMap;
    mutate(mapSenzaAdmin as RolePermissionsMap, {
      onSuccess: () => {
        // Reset la copia locale: torna a mostrare i dati server aggiornati
        setLocalMap(null);
      },
    });
  }

  function handleCreateRole(e: React.FormEvent) {
    e.preventDefault();
    if (!newRoleName) return;
    createRoleMutation.mutate(
      { name: newRoleName },
      {
        onSuccess: () => {
          setNewRoleName('');
        },
      },
    );
  }

  return (
    <div className={styles.section} data-testid="sezione-rbac">
      <p className={styles.sectionTitle}>Privilegi RBAC per ruolo</p>
      <p className={styles.sectionSubtitle}>
        Definisce quali privilegi di autorizzazione sono concessi a ciascun ruolo CRA. Applicati
        server-side da tutti i servizi tenaciousc; le modifiche si propagano entro pochi secondi.
      </p>

      {!puoConfigurare && (
        <p className={styles.sectionSubtitle} data-testid="rbac-readonly">
          Solo un amministratore può modificare i privilegi.
        </p>
      )}

      {puoConfigurare && isLoadingRbac && (
        <p className={styles.loadingMsg} data-testid="rbac-loading">
          Caricamento privilegi...
        </p>
      )}

      {puoConfigurare && isErrorRbac && (
        <div className={styles.errorMsg} data-testid="rbac-error">
          {rbacQueryError instanceof ApiError
            ? rbacQueryError.message
            : (rbacQueryError?.message ?? 'Errore caricamento privilegi')}
        </div>
      )}

      {puoConfigurare && !isLoadingRbac && !isErrorRbac && displayMap && (
        <>
          {/* Privilegi canonici Resource.Operation, raggruppati per risorsa (Task 6). */}
          <table className={styles.capabilitiesTable} data-testid="rbac-matrice">
            <thead>
              <tr>
                <th>Privilegio</th>
                {orderedRoles.map((role) => (
                  <th key={role}>{ROLE_LABELS[role] ?? role}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {canonicalGroups.map(({ resource, permissions }) => (
                <Fragment key={resource}>
                  <tr data-testid={`rbac-group-${resource}`}>
                    <td colSpan={orderedRoles.length + 1}>
                      <strong>{resource}</strong>
                    </td>
                  </tr>
                  {permissions.map((perm) => (
                    <RbacRow
                      key={perm}
                      perm={perm}
                      label={canonicalOperationLabel(perm)}
                      orderedRoles={orderedRoles}
                      displayMap={displayMap}
                      puoConfigurare={puoConfigurare}
                      isPending={isPending}
                      onToggle={handleToggle}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* Privilegi legacy DT (vocabolario storico) — gruppo collassato, F-05.2 / Task 6. */}
          <details className={styles.section} data-testid="rbac-legacy-group">
            <summary>Permessi legacy ({legacyPermissions.length})</summary>
            <table className={styles.capabilitiesTable} data-testid="rbac-matrice-legacy">
              <thead>
                <tr>
                  <th>Privilegio</th>
                  {orderedRoles.map((role) => (
                    <th key={role}>{ROLE_LABELS[role] ?? role}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {legacyPermissions.map((perm) => (
                  <RbacRow
                    key={perm}
                    perm={perm}
                    label={TC_PERMISSION_LABELS[perm] ?? perm}
                    orderedRoles={orderedRoles}
                    displayMap={displayMap}
                    puoConfigurare={puoConfigurare}
                    isPending={isPending}
                    onToggle={handleToggle}
                  />
                ))}
              </tbody>
            </table>
          </details>

          <div className={styles.formRow}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSalva}
              disabled={isPending || localMap === null}
              data-testid="btn-salva-rbac"
            >
              {isPending ? 'Salvataggio...' : 'Salva'}
            </button>
            {mutError && (
              <span className={styles.inlineMutError} data-testid="rbac-mut-error">
                {mutErrObj instanceof ApiError
                  ? mutErrObj.message
                  : (mutErrObj?.message ?? 'Errore salvataggio')}
              </span>
            )}
            {isSuccess && (
              <span className={styles.successMsg} data-testid="rbac-success">
                Privilegi aggiornati.
              </span>
            )}
          </div>

          {/* Form creazione nuovo ruolo custom */}
          <form onSubmit={handleCreateRole} className={styles.newRoleForm}>
            <label htmlFor="new-role-name">Nuovo ruolo</label>
            <input
              id="new-role-name"
              value={newRoleName}
              pattern="[a-z][a-z0-9_]{2,63}"
              onChange={(e) => setNewRoleName(e.target.value)}
            />
            <button type="submit" disabled={createRoleMutation.isPending}>
              Crea ruolo
            </button>
            <p className={styles.hint}>
              Gli utenti ottengono il ruolo tramite il gruppo IdP{' '}
              <code>tenaciousc-&lt;nome-ruolo&gt;</code>.
            </p>
          </form>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: branding (logo portale)
// ---------------------------------------------------------------------------

/** Dimensione massima del file logo caricato (512 KB). */
const MAX_LOGO_FILE_BYTES = 512 * 1024;

/** Converte un File in data URL (base64) via FileReader. */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Impossibile leggere il file'));
    reader.readAsDataURL(file);
  });
}

function SezioneBranding({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data, isLoading, isError, error: queryError } = useBranding();
  const { mutate, isPending, isError: mutError, error: mutErrObj, isSuccess } = useUpdateBranding();

  const [urlInput, setUrlInput] = useState('');
  // draft = valore da salvare; null = nessuna modifica rispetto al server.
  const [draft, setDraft] = useState<string | null>(null);
  const [fileError, setFileError] = useState('');

  const current = data?.logoUrl ?? null;
  const preview = draft !== null ? draft : current;
  const edited = draft !== null && draft !== (current ?? '');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError('');
    if (!file.type.startsWith('image/')) {
      setFileError('Seleziona un file immagine.');
      return;
    }
    if (file.size > MAX_LOGO_FILE_BYTES) {
      setFileError('Immagine troppo grande (max 512 KB).');
      return;
    }
    try {
      setDraft(await fileToDataUrl(file));
    } catch {
      setFileError('Impossibile leggere il file.');
    }
  }

  return (
    <div className={styles.section} data-testid="sezione-branding">
      <p className={styles.sectionTitle}>Logo aziendale</p>
      <p className={styles.sectionSubtitle}>
        Logo mostrato nell&apos;header del portale. Incolla un URL pubblico (http/https) oppure
        carica un&apos;immagine (max 512 KB).
      </p>

      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="branding-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="branding-loading">
          Caricamento configurazione...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="branding-error">
          {queryError instanceof ApiError
            ? queryError.message
            : (queryError?.message ?? 'Errore caricamento branding')}
        </div>
      )}

      {!isLoading && !isError && puoConfigurare && (
        <>
          <div className={styles.formRow}>
            <div className={styles.formGroup} style={{ flex: 1, minWidth: '18rem' }}>
              <label className={styles.formGroupLabel} htmlFor="input-logo-url">
                URL logo
              </label>
              <input
                id="input-logo-url"
                type="url"
                className={styles.formInput}
                placeholder="https://esempio.com/logo.png"
                value={
                  draft !== null
                    ? draft.startsWith('data:')
                      ? ''
                      : draft
                    : (current && !current.startsWith('data:') ? current : '') || urlInput
                }
                onChange={(e) => {
                  setUrlInput(e.target.value);
                  setDraft(e.target.value);
                }}
                data-testid="input-logo-url"
                disabled={isPending}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="input-logo-file">
                …oppure carica un file
              </label>
              <input
                id="input-logo-file"
                type="file"
                accept="image/*"
                onChange={(e) => void handleFile(e)}
                data-testid="input-logo-file"
                disabled={isPending}
              />
            </div>
          </div>

          {fileError && (
            <span className={styles.inlineMutError} data-testid="branding-file-error">
              {fileError}
            </span>
          )}

          {preview && (
            <div className={styles.formRow} style={{ alignItems: 'center' }}>
              <span className={styles.formGroupLabel}>Anteprima:</span>
              <img
                src={preview}
                alt="Anteprima logo"
                data-testid="branding-preview"
                style={{ height: '38px', maxWidth: '220px', objectFit: 'contain' }}
              />
            </div>
          )}

          <div className={styles.formRow}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => mutate(draft)}
              disabled={isPending || !edited}
              data-testid="btn-salva-branding"
            >
              {isPending ? 'Salvataggio...' : 'Salva logo'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setUrlInput('');
                setDraft('');
              }}
              disabled={isPending || (!current && draft === null)}
              data-testid="btn-rimuovi-branding"
            >
              Rimuovi logo
            </button>
            {mutError && (
              <span className={styles.inlineMutError} data-testid="branding-mut-error">
                {mutErrObj instanceof ApiError
                  ? mutErrObj.message
                  : (mutErrObj?.message ?? 'Errore salvataggio')}
              </span>
            )}
            {isSuccess && (
              <span className={styles.successMsg} data-testid="branding-success">
                Logo aggiornato.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: Danger Zone (eliminazione catalogo prodotti)
// ---------------------------------------------------------------------------

const CONFERMA_ELIMINA_CATALOGO =
  'Eliminare TUTTO il catalogo prodotti (Product, Release, bundle, BOM, mapping)?\n' +
  "Questa azione è distruttiva e non è annullabile dall'interfaccia.";

function SezioneDangerZone({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { mutate, isPending, isError, error, isSuccess, data } = useDeleteProductCatalog();

  function handleClick() {
    if (!window.confirm(CONFERMA_ELIMINA_CATALOGO)) return;
    mutate();
  }

  return (
    <div className={`${styles.section} ${styles.dangerSection}`} data-testid="sezione-danger-zone">
      <p className={styles.sectionTitle}>Danger Zone</p>
      <p className={styles.sectionSubtitle}>
        Azioni distruttive e irreversibili. Procedi solo se sai cosa stai facendo.
      </p>

      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="danger-zone-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}

      {puoConfigurare && (
        <div className={styles.formRow}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleClick}
            disabled={isPending}
            data-testid="btn-elimina-catalogo"
          >
            {isPending ? 'Eliminazione...' : 'Elimina catalogo prodotti'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="danger-zone-error">
              {error instanceof ApiError
                ? error.message
                : (error?.message ?? 'Errore eliminazione')}
            </span>
          )}
          {isSuccess && (
            <span className={styles.successMsg} data-testid="danger-zone-success">
              Catalogo eliminato ({data.deleted} product{data.deleted === 1 ? '' : 's'},{' '}
              {data.triagesDeleted} triage collegat{data.triagesDeleted === 1 ? 'o' : 'i'}).
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione 0: strumenti dell'ambiente (Mailpit, UI Dependency-Track)
// ---------------------------------------------------------------------------

function SezioneStrumenti({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data, isLoading, isError, error } = useAdminTools(puoConfigurare);
  if (!puoConfigurare) return null;

  const host = window.location.hostname;
  const nessuno = data && !data.mailpitUrl && !data.dtUiUrl;

  return (
    <div className={styles.section} data-testid="sezione-strumenti">
      <p className={styles.sectionTitle}>Strumenti dell&apos;ambiente</p>
      <p className={styles.sectionSubtitle}>
        Accesso rapido alle UI di supporto dell&apos;ambiente (si aprono in una nuova scheda).
      </p>
      {isLoading && <p className={styles.loadingMsg}>Caricamento…</p>}
      {isError && (
        <div className={styles.errorMsg} data-testid="strumenti-errore">
          {error instanceof ApiError ? error.message : (error?.message ?? 'Errore')}
        </div>
      )}
      {nessuno && (
        <p className={styles.sectionSubtitle} data-testid="strumenti-non-configurati">
          Nessuno strumento configurato per questo ambiente.
        </p>
      )}
      {data && !nessuno && (
        <div className={styles.toolLinks}>
          {data.mailpitUrl && (
            <a
              href={resolveToolUrl(data.mailpitUrl, host)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.toolLink}
              title="Mailpit — email inviate dall'ambiente"
              data-testid="link-mailpit"
            >
              Mailpit
            </a>
          )}
          {data.dtUiUrl && (
            <span className={styles.toolLinkGroup}>
              <a
                href={resolveToolUrl(data.dtUiUrl, host)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.toolLink}
                title="Dependency-Track UI (utente admin)"
                data-testid="link-dtui"
              >
                DTUI
              </a>
              <CopyButton
                getText={fetchDtAdminPassword}
                label="Copia password admin DT"
                testId="btn-copia-password-dt"
                disabled={!data.dtAdminPasswordAvailable}
              />
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SOLO DEV — DA RIMUOVERE PRIMA DELLA PRODUZIONE: contenuto del file .env del server
// ---------------------------------------------------------------------------

/**
 * Mostra agli admin il contenuto integrale del `.env` del server (segreti inclusi), come richiesto
 * per lo sviluppo. Compare solo se il backend ha il visualizzatore acceso (`ADMIN_ENV_VIEWER=1`,
 * solo compose dev); il contenuto è letto solo al click e ogni lettura finisce nel log audit.
 */
function SezioneEnvFile({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data: tools } = useAdminTools(puoConfigurare);
  const [aperto, setAperto] = useState(false);
  const abilitato = puoConfigurare && tools?.envViewerEnabled === true;
  const { data, isFetching, isError, error } = useAdminEnvFile(abilitato && aperto);
  if (!abilitato) return null;

  return (
    <div className={`${styles.section} ${styles.dangerSection}`} data-testid="sezione-env-file">
      <p className={styles.sectionTitle}>File .env del server</p>
      <div className={styles.accessDenied} role="note" data-testid="banner-env-solo-dev">
        <strong>SOLO SVILUPPO — questa sezione verrà rimossa prima della produzione.</strong> Mostra
        il contenuto integrale del file <code>.env</code> del server (
        <strong>segreti inclusi</strong>). Ogni lettura è registrata nel log audit.
      </div>
      <div className={styles.formRow}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => setAperto((v) => !v)}
          aria-expanded={aperto}
          data-testid="btn-mostra-env-file"
        >
          {aperto ? 'Nascondi contenuto' : 'Mostra contenuto .env'}
        </button>
      </div>
      {aperto && isFetching && <p className={styles.loadingMsg}>Lettura del file…</p>}
      {aperto && isError && (
        <div className={styles.errorMsg} data-testid="env-file-errore">
          {error instanceof ApiError ? error.message : (error?.message ?? 'Errore')}
        </div>
      )}
      {aperto && data && (
        <>
          <p className={styles.sectionSubtitle} data-testid="env-file-path">
            {data.path}
          </p>
          <pre className={styles.envFileContent} data-testid="env-file-content">
            {data.content}
          </pre>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione 3: chiave NVD (istruzioni — RF-12-1 è nativo DT)
// ---------------------------------------------------------------------------

function SezioneNvd() {
  return (
    <div className={styles.section} data-testid="sezione-nvd">
      <p className={styles.sectionTitle}>Chiave API NVD (RF-12-1)</p>
      <p className={styles.sectionSubtitle}>
        RF-12-1 è gestito nativamente da Dependency-Track. Non è necessario configurare nulla nel
        portale Tenacious C.
      </p>
      <div className={styles.infoBox} data-testid="nvd-istruzioni">
        <p>
          <strong>Come configurare la chiave NVD in Dependency-Track:</strong>
        </p>
        <p>1. Accedi al pannello di Dependency-Track (amministratore DT).</p>
        <p>
          2. Vai a: <strong>Administration → Vulnerability Sources → NVD</strong>
        </p>
        <p>
          3. Inserisci la tua API key NVD nel campo <em>NVD API Key</em> e salva.
        </p>
        <p>
          4. Una chiave NVD velocizza l'aggiornamento del feed CVE da NVD. Senza chiave, DT usa
          comunque il feed pubblico ma con rate limiting.
        </p>
        <p>
          Ottieni una chiave gratuita su:{' '}
          <a
            href="https://nvd.nist.gov/developers/request-an-api-key"
            target="_blank"
            rel="noopener noreferrer"
          >
            nvd.nist.gov/developers/request-an-api-key
          </a>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sezione: schedule Prefect (cron dei job + pausa/ripresa)
// ---------------------------------------------------------------------------

/** Una riga della tabella schedule: stato locale di modifica + salvataggio per-deployment. */
function RigaDeployment({ dep }: { dep: PrefectDeployment }) {
  const [cron, setCron] = useState(dep.cron ?? '');
  const [active, setActive] = useState(dep.active);
  const { mutate, isPending, isError, error, isSuccess, reset } = useUpdateDeploymentSchedule();

  const dirty = cron !== (dep.cron ?? '') || active !== dep.active;

  return (
    <tr data-testid={`prefect-row-${dep.name}`}>
      <td>
        <span style={{ fontWeight: 600 }}>{dep.name}</span>
        {dep.overridden && (
          <span
            data-testid={`prefect-overridden-${dep.name}`}
            style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#6c757d' }}
          >
            (override)
          </span>
        )}
      </td>
      <td>
        <input
          type="text"
          className={styles.formInput}
          value={cron}
          onChange={(e) => {
            setCron(e.target.value);
            reset();
          }}
          placeholder="es. 0 1 * * *"
          aria-label={`Cron per ${dep.name}`}
          data-testid={`prefect-cron-${dep.name}`}
          style={{ fontFamily: 'monospace', minWidth: '9rem' }}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => {
            setActive(e.target.checked);
            reset();
          }}
          aria-label={`Attivo per ${dep.name}`}
          data-testid={`prefect-active-${dep.name}`}
        />
      </td>
      <td>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={isPending || !dirty || cron.trim() === ''}
          onClick={() => mutate({ id: dep.id, name: dep.name, cron: cron.trim(), active })}
          data-testid={`prefect-save-${dep.name}`}
        >
          {isPending ? 'Salvataggio...' : 'Salva'}
        </button>
        {isError && (
          <span className={styles.inlineMutError} data-testid={`prefect-error-${dep.name}`}>
            {error instanceof ApiError ? error.message : (error?.message ?? 'Errore')}
          </span>
        )}
        {isSuccess && !dirty && (
          <span className={styles.successMsg} data-testid={`prefect-success-${dep.name}`}>
            Aggiornato.
          </span>
        )}
      </td>
    </tr>
  );
}

function SezionePrefectSchedules({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data, isLoading, isError, error } = usePrefectDeployments(puoConfigurare);

  return (
    <div className={styles.section} data-testid="sezione-prefect-schedules">
      <p className={styles.sectionTitle}>Schedule job Prefect (cron)</p>
      <p className={styles.sectionSubtitle}>
        Modifica la pianificazione cron dei job automatici (e mettili in pausa/ripresa). Le
        modifiche sono applicate subito e sopravvivono ai reset dell'ambiente.
      </p>
      {!puoConfigurare && (
        <div className={styles.accessDenied} data-testid="prefect-access-denied">
          Accesso negato: è richiesto il ruolo <strong>admin</strong>.
        </div>
      )}
      {puoConfigurare && isLoading && (
        <p className={styles.loadingMsg} data-testid="prefect-loading">
          Caricamento deployment…
        </p>
      )}
      {puoConfigurare && isError && (
        <div className={styles.errorMsg} data-testid="prefect-error">
          Errore nel caricamento dei deployment Prefect:{' '}
          {error instanceof ApiError ? error.message : (error?.message ?? 'sconosciuto')}
        </div>
      )}
      {puoConfigurare && !isLoading && !isError && Array.isArray(data) && (
        <table className={styles.capabilitiesTable} data-testid="prefect-table">
          <thead>
            <tr>
              <th>Deployment</th>
              <th>Cron</th>
              <th>Attivo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((dep) => (
              <RigaDeployment key={dep.id} dep={dep} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function AdminPage() {
  const { can } = usePermissions();
  const puoConfigurare = can('SYSTEM_CONFIGURATION');

  return (
    <div data-testid="admin-page">
      {/* Intestazione */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Amministrazione</h1>
        <span className={styles.pageSubtitle}>E10.11 / RF-7-1 + RF-12-1</span>
      </div>

      {/* Sezione 1: soglia notifiche */}
      <SezioneThreshold puoConfigurare={puoConfigurare} />

      {/* Sezione 2: permessi portale per ruolo */}
      <SezionePermessi puoConfigurare={puoConfigurare} />

      <SezioneRbac puoConfigurare={puoConfigurare} />

      {/* Login Microsoft Entra ID (oidc-client-ts) e mapping ruoli: assente senza AUTH_BROKER_ENABLED */}
      <SezioneSso puoConfigurare={puoConfigurare} />

      {/* Sezione: soglia di hold della Batch Edit API */}
      <SezioneBatchEditThreshold puoConfigurare={puoConfigurare} />

      {/* Sezione: parole minime della nota per il cliente (chiusura Ticket manuali) */}
      <SezioneTicketNotaCliente puoConfigurare={puoConfigurare} />

      {/* Sezione: logo aziendale (branding header) */}
      <SezioneBranding puoConfigurare={puoConfigurare} />

      {/* Sezione: schedule cron dei job Prefect */}
      <SezionePrefectSchedules puoConfigurare={puoConfigurare} />

      {/* Strumenti dell'ambiente: link Mailpit / DTUI (solo admin) */}
      <SezioneStrumenti puoConfigurare={puoConfigurare} />

      {/* SOLO DEV, da rimuovere prima della produzione: contenuto del .env (solo admin) */}
      <SezioneEnvFile puoConfigurare={puoConfigurare} />

      {/* Sezione 3: chiave NVD (solo istruzioni, no API) */}
      <SezioneNvd />

      {/* Danger Zone: azioni distruttive, in fondo alla pagina */}
      <SezioneDangerZone puoConfigurare={puoConfigurare} />
    </div>
  );
}
